import { useEffect, useRef } from 'react';

export function useJWAdTimer() {
  const timerRef = useRef<HTMLDivElement | null>(null);
  const valueNodeRef = useRef<Text | null>(null);
  const lastValueRef = useRef<string>('');
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    function ensurePlayer(): HTMLElement | null {
      return document.querySelector('.jwplayer');
    }

    function createTimer(jwPlayer: HTMLElement) {
      if (timerRef.current) { return timerRef.current; }

      // Ensure parent positioning
      if (getComputedStyle(jwPlayer).position === 'static') {
        jwPlayer.style.position = 'relative';
      }

      const el = document.createElement('div');
      el.className = 'adCountdownOverlay';
      el.style.display = 'none';

      const label = document.createElement('span');
      label.textContent = 'Ad';

      const valueNode = document.createTextNode('');
      el.appendChild(label);
      el.appendChild(valueNode);

      jwPlayer.appendChild(el);

      timerRef.current = el;
      valueNodeRef.current = valueNode;

      return el;
    }

    function update() {
      const jwPlayer = ensurePlayer();
      if (!jwPlayer) {
        rafRef.current = requestAnimationFrame(update);
        return;
      }

      const isAd = jwPlayer.classList.contains('jw-flag-ads');
      const jwCountdown = jwPlayer.querySelector('.jw-text-countdown') as HTMLElement | null;

      if (!isAd || !jwCountdown) {
        if (timerRef.current) { timerRef.current.style.display = 'none'; }
        rafRef.current = requestAnimationFrame(update);
        return;
      }

      const value = jwCountdown.textContent?.trim();
      if (value && value !== lastValueRef.current) {
        lastValueRef.current = value;

        const el = createTimer(jwPlayer);
        if (valueNodeRef.current) {
          valueNodeRef.current.nodeValue = ` ${value}`;
        }

        el.style.display = 'flex';
      }

      rafRef.current = requestAnimationFrame(update);
    }

    update();

    return () => {
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); }
      rafRef.current = null;

      timerRef.current?.remove();
      timerRef.current = null;
      valueNodeRef.current = null;
    };
  }, []);
}

export function getSSAIAdState(
  playerTime: number,
  ssai: {
    isLive: boolean;
    prerollDuration: number;
    adBreakDuration: number;
    cuePoints: number[];
  },
): {
  isAd: boolean;
  type?: 'preroll' | 'midroll';
  index?: number;
  remaining?: number;
  start?: number;
  end?: number;
} {
  const { prerollDuration, adBreakDuration, cuePoints, isLive } = ssai;

  // 1️⃣ PREROLL
  if (playerTime >= 0 && playerTime < prerollDuration) {
    return {
      isAd: true,
      type: 'preroll',
      index: 0,
      start: 0,
      end: prerollDuration,
      remaining: Math.ceil(prerollDuration - playerTime),
    };
  }

  // 2️⃣ MIDROLLS (predictive)
  let accumulatedAdTime = prerollDuration;

  for (let i = 1; i < cuePoints.length; i++) {
    const cue = cuePoints[i];

    const start = cue + accumulatedAdTime;
    const end = start + adBreakDuration;

    if (playerTime >= start && playerTime < end) {
      return {
        isAd: true,
        type: 'midroll',
        index: i, // 1-based midroll index
        start,
        end,
        remaining: Math.ceil(end - playerTime),
      };
    }

    accumulatedAdTime += adBreakDuration;
  }

  return { isAd: false };
}
